import { type FormEvent, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api, messageOf } from "../lib/api.ts";
import { useToken } from "../lib/auth.tsx";
import type { AgentOutlet } from "./AgentLayout.tsx";

export function Look() {
  const { agent, reload, mark, liveTheme, setLiveTheme } = useOutletContext<AgentOutlet>();
  const token = useToken();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  function update(patch: Partial<typeof liveTheme>) {
    setLiveTheme({ ...liveTheme, ...patch });
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      await api(`/v1/agents/${agent.id}`, token, {
        method: "PATCH",
        json: {
          theme: {
            color: liveTheme.color,
            greeting: liveTheme.greeting,
            title: liveTheme.title?.trim() || null,
            avatarUrl: liveTheme.avatarUrl?.trim() || null,
          },
        },
      });
      mark("look");
      await reload();
      navigate("../install");
    } catch (caught) {
      setError(messageOf(caught));
      setPending(false);
    }
  }

  return (
    <form className="card stack" onSubmit={onSubmit}>
      <header className="step-head">
        <p className="eyebrow">Step 5 of 6</p>
        <h1>Customize the look</h1>
        <p className="lead">The chat bubble updates as you edit. Save to keep it. Visitors see this color, greeting, and picture.</p>
      </header>
      <div className="split">
        <label>
          Color
          <input type="color" value={safeColor(liveTheme.color)} onChange={(event) => update({ color: event.target.value })} />
        </label>
        <label>
          Hex
          <input value={liveTheme.color} pattern="#[0-9a-fA-F]{6}" required onChange={(event) => update({ color: event.target.value })} />
        </label>
      </div>
      <label>
        Greeting
        <input required maxLength={240} value={liveTheme.greeting} onChange={(event) => update({ greeting: event.target.value })} />
      </label>
      <p className="muted">This is the first message in the chat.</p>
      <label>
        Title
        <input maxLength={80} value={liveTheme.title ?? ""} placeholder={agent.name} onChange={(event) => update({ title: event.target.value })} />
      </label>
      <label>
        Picture address
        <input
          type="url"
          value={liveTheme.avatarUrl ?? ""}
          placeholder="https://example.com/avatar.png"
          onChange={(event) => update({ avatarUrl: event.target.value })}
        />
      </label>
      <p className="muted">Optional. Use a full https address for a logo or photo. Leave it blank to show the first letter.</p>
      {error ? <p className="banner bad">{error}</p> : null}
      <button className="button block" type="submit" disabled={pending}>
        {pending ? "Saving…" : "Save and continue"}
      </button>
    </form>
  );
}

function safeColor(value: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#ef5b25";
}
